import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps
} from '@mui/x-date-pickers';
import { InputBaseComponentProps, Theme } from '@mui/material';
import { InputProps as MUIInputProps } from '@mui/material/Input/Input';
import { addYears, format } from 'date-fns';
import i18next from 'i18next';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { TextField, TextFieldProps } from '../textField';
import { Calendar, Down, Next, Previous } from './icons';
import { languageToFormat } from './languageSpecifics';
import { PickersDay } from './pickersDay';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { CustomActionBar } from './actionBar';

export type Variant = 'date' | 'month' | 'year';
export type Size = 'small' | 'normal';

const DEFAULT_YEAR_RANGE = 10;

type Props = Omit<
  MuiDatePickerProps<Date, Date>,
  'onChange' | 'value' | 'renderInput' | 'views'
> & {
  onChange: (isoString: string) => void;
  onChangeInvalidDate?: (invalidInput: string | undefined) => void;
  label?: string;
  initialValue?: string | null;
  variant?: Variant;
  size?: Size;
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  required?: TextFieldProps['required'];
  placeholder?: string;
  renderInput?: (
    inputRef: React.Ref<HTMLInputElement> | undefined,
    inputProps: InputBaseComponentProps | undefined,
    InputProps: Partial<MUIInputProps> | undefined
  ) => JSX.Element;
  missing?: boolean;
  missingMessage?: string;
  futureEditingLocked?: boolean;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onClear?: () => void;
  yearRange?: number;
};

export type DatePickerProps = Props;

export const DatePicker: FC<Props> = ({
  onChange,
  onChangeInvalidDate,
  label,
  initialValue,
  variant = 'date',
  size = 'normal',
  error,
  helperText,
  placeholder,
  renderInput,
  missing,
  missingMessage,
  futureEditingLocked,
  open,
  onOpen,
  onClose,
  onClear,
  yearRange = DEFAULT_YEAR_RANGE,
  minDate: minDateProp,
  maxDate: maxDateProp,
  ...datePickerProps
}) => {
  const [value, setValue] = useState<Date | null>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isOpen = open !== undefined ? open : internalIsOpen;

  const { t } = useTranslation();

  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    return {
      minDate: minDateProp ?? addYears(today, -yearRange),
      maxDate: maxDateProp ?? addYears(today, yearRange)
    };
  }, [yearRange, minDateProp, maxDateProp]);

  useEffect(() => {
    const initialDate = initialValue ? new Date(initialValue) : null;
    setValue(initialDate);
  }, [initialValue]);

  const localeLanguage = i18next.language === 'de' ? de : undefined;

  const handleOpen = () => {
    if (open === undefined) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  };

  const handleClose = () => {
    if (open === undefined) {
      setInternalIsOpen(false);
    }
    onClose?.();
  };

  return (
    <LocalizationProvider
      locale={localeLanguage}
      localeText={{
        clearButtonLabel: t('common:RESET')
      }}
      dateAdapter={AdapterDateFns}
    >
      <MuiDatePicker<Date>
        {...datePickerProps}
        minDate={minDate}
        maxDate={maxDate}
        disableHighlightToday={variant === 'month'}
        autoFocus={false}
        components={{
          LeftArrowIcon: Previous,
          RightArrowIcon: Next,
          SwitchViewIcon: Down,
          OpenPickerIcon: Calendar,
          ActionBar: CustomActionBar
        }}
        onChange={(date, keyboardValue) => {
          setValue(date);
          if (date === null) {
            onClear?.();
            return;
          }
          try {
            onChange(format(date as Date, 'yyyy-MM-dd'));
          } catch {
            onChangeInvalidDate?.(keyboardValue);
          }
        }}
        componentsProps={{
          actionBar: {
            actions: ['clear']
          }
        }}
        value={value}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        renderDay={(_, __, pickersDayProps) => <PickersDay {...pickersDayProps} />}
        renderInput={({ inputRef, inputProps, InputProps }) => {
          if (renderInput) {
            return renderInput(inputRef, inputProps, InputProps);
          }

          return (
            <TextField
              label={label}
              missing={missing}
              missingMessage={missingMessage}
              inputProps={{
                ref: inputRef,
                ...inputProps,
                placeholder: placeholder ?? '',
                value: value ? inputProps?.value : '',
                className: 'placeholder:!text-grey-500 placeholder:!opacity-75 cursor-pointer',
                readOnly: true
              }}
              endAdornment={InputProps?.endAdornment}
              error={error}
              helperText={error ? helperText : undefined}
              required={datePickerProps.required}
              disabled={datePickerProps.disabled}
              futureEditingLocked={futureEditingLocked}
              onClick={handleOpen}
            />
          );
        }}
        PaperProps={{
          className: 'shadow-500',
          sx: (theme: Theme) => ({
            padding: '8px',
            '& .MuiCalendarPicker-root': {
              width: 'auto',
              maxHeight: 'none',
              overflow: 'hidden'
            },

            '& .PrivatePickersMonth-root': {
              height: '48px',
              borderRadius: '8px',
              fontWeight: 'normal',
              fontSize: '16px',
              margin: 0,
              '&:hover': {
                backgroundColor: theme.palette.grey[50]
              },
              '&:disabled': {
                color: `${theme.palette.grey[300]} !important`
              },
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.heaven.main} !important`,
                color: theme.palette.common.white,
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: `${theme.palette.heaven.main} !important`
                }
              }
            },
            '& .MuiPickersCalendarHeader-root': {
              marginBottom: '16px',
              paddingLeft: '8px',
              paddingRight: '8px'
            },

            '& .MuiPickersCalendarHeader-label': {
              fontSize: '18px',
              fontWeight: '600',
              color: theme.palette.grey[900]
            },
            '& .PrivateDatePickerToolbar-penIcon': {
              display: 'none'
            },
            '& .PrivatePickersYear-yearButton': {
              borderRadius: '8px',
              fontWeight: 'normal',
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.heaven.main} !important`
              }
            },
            '& .MuiPickersDay-root.Mui-selected': {
              backgroundColor: `${theme.palette.heaven.main} !important`,
              '&.MuiPickersDay-today': {
                color: `${theme.palette.common.white}`
              }
            },
            '& .MuiPickersDay-root.MuiPickersDay-today': {
              color: `${theme.palette.heaven.main}`,
              backgroundColor: `${theme.palette.grey['50']}`
            }
          })
        }}
        {...languageToFormat(i18next.language, variant, size)}
      />
    </LocalizationProvider>
  );
};
